import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AddBoardQuestion from "./AddBoardQuestion";
import Table from "../../Table";
import {
  fetchBoardQuestions,
  fetchQuestionById,
  setEditData,
} from "../redux/boardQuestionSlice";
import BoardCascadingDropdowns from "./BoardCascadingDropdowns";
import UploadQuestionsTab from "./UploadQuestionsTab";
import API_BASE_URL from "../../config";
import showToast from "../../../utils/toast";
import { Toaster } from "react-hot-toast";

const QuestionBank = () => {
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const [showAddQuestionPopup, setShowAddQuestionPopup] = useState(false);
  const [showUploadQuestionPopup, setShowUploadQuestionPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    questions,
    currentPage,
    totalPages,
    totalQuestions,
    selectedMedium,
    selectedClass,
    selectedSubject,
    questionsLoading,
    selectedChapter,
    editData,
    editDataLoading,
  } = useSelector((state) => state.boardQuestion);

  // Fetch questions on component mount and when page changes
  useEffect(() => {
    if (boardId && selectedMedium && selectedClass && selectedSubject) {
      dispatch(
        fetchBoardQuestions({
          board_id: boardId,
          page: currentPage,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
          chapter_id: selectedChapter,
          // question_typeid: "",
        }),
      );
    }
  }, [
    dispatch,
    boardId,
    currentPage,
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
  ]);

  // board_id,
  //     medium_id,
  //     class_id,
  //     subject_id,
  //     question_typeid,
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(
        fetchBoardQuestions({
          board_id: boardId,
          page: newPage,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
          chapter_id: selectedChapter,
          // question_typeid: "",
        }),
      );
    }
  };

  const handleRefreshQuestions = () => {
    dispatch(
      fetchBoardQuestions({
        board_id: boardId,
        page: currentPage,
        medium_id: selectedMedium,
        class_id: selectedClass,
        subject_id: selectedSubject,
        chapter_id: selectedChapter,
      }),
    );
  };

  const handleDeleteQuestion = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this question? This action cannot be undone.",
      )
    )
      return;

    setIsLoading(true);
    const toastId = showToast.loading("Deleting question...");
    try {
      const response = await fetch(`${API_BASE_URL}/questions/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! Status: ${response.status}`,
        );
      }

      showToast.dismiss(toastId);
      showToast.success("Question deleted successfully!");
      handleRefreshQuestions();
    } catch (err) {
      console.error("Error deleting question:", err);
      showToast.dismiss(toastId);
      showToast.error(`Failed to delete question: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditQuestion = async (questionId) => {
    try {
      await dispatch(fetchQuestionById(questionId)).unwrap();
      setShowAddQuestionPopup(true);
    } catch (err) {
      console.error("Error fetching question:", err);
      showToast.error("Failed to load question for editing.");
    }
  };

  const disableValue =
    !selectedMedium || !selectedClass || !selectedSubject || !selectedChapter;

  // Table columns
  const columns = [
    {
      header: "Question",
      accessor: "question_text",
      render: (value) => (
        <div
          style={{
            maxWidth: "400px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={value}
        >
          {value || ""}
        </div>
      ),
    },
    {
      header: "Medium",
      accessor: "medium_name",
    },
    {
      header: "Class",
      accessor: "class_name",
    },
    {
      header: "Subject",
      accessor: "subject_name",
    },
    {
      header: "Chapter",
      accessor: "chapter_name",
    },
    {
      header: "Actions",
      accessor: "question_id",
      align: "center",
      render: (value, row) => (
        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
          <button
            onClick={() => handleEditQuestion(row.question_id)}
            disabled={isLoading}
            className="btn btn-sm btn-outline-primary"
            title="Edit"
            style={{
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
          <button
            onClick={() => handleDeleteQuestion(row.question_id)}
            disabled={isLoading}
            className="btn btn-sm btn-outline-danger"
            title="Delete"
            style={{
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
      <Toaster position="top-right" />
      <style>
        {`
          .pagination-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-top: 24px;
            padding: 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          .pagination-btn {
            background: white;
            border: 1px solid #d1d5db;
            color: #374151;
            padding: 8px 14px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            min-width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .pagination-btn:hover:not(:disabled) {
            background: #f3f4f6;
            border-color: #3498db;
            color: #3498db;
          }

          .pagination-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .pagination-btn.active {
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
            border-color: #3498db;
            font-weight: 600;
          }

          .pagination-info {
            padding: 8px 16px;
            background: #f8f9fa;
            border-radius: 6px;
            font-size: 13px;
            color: #6b7280;
            font-weight: 500;
          }

          .table-scroll-container::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .table-scroll-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }

          .table-scroll-container::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .table-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        .buttonStyle {
  background-color: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.disabled-buttonStyle {
  background-color: gray;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: not-allowed;
  opacity: 0.6;
}
        `}
      </style>

      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        {/* Left section */}
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Question Bank
          </h2>
          <p
            style={{
              margin: "4px 0 0 0",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            {/* ? `Total ${totalQuestions} question${totalQuestions !== 1 ? "s" : ""}` */}
            {totalQuestions > 0
              ? `Total Question${totalQuestions !== 1 ? "s" : ""} Count: ${totalQuestions} `
              : "No questions available"}
          </p>
        </div>

        {/* Right section – buttons */}
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => {
              dispatch(setEditData(null));
              setShowAddQuestionPopup(true);
            }}
            className={!disableValue ? "buttonStyle" : "disabled-buttonStyle"}
            disabled={disableValue}
          >
            <span style={{ fontSize: "18px" }}>+</span> Add Question
          </button>

          <button
            onClick={() => setShowUploadQuestionPopup(true)}
            className={!disableValue ? "buttonStyle" : "disabled-buttonStyle"}
            disabled={disableValue}
          >
            <span style={{ fontSize: "18px" }}>📤</span> Upload Questions
          </button>
        </div>
      </div>
      <BoardCascadingDropdowns
        boardId={boardId}
        showTitle={true}
        layout="main"
      />

      {/* QUESTIONS TABLE */}
      {questionsLoading ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
          <p style={{ fontSize: "16px", color: "#6b7280" }}>
            Loading questions...
          </p>
        </div>
      ) : questions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "8px",
            }}
          >
            No Questions Found
          </h3>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Click "Add Question" to start adding questions to your question
            bank.
          </p>
        </div>
      ) : (
        <>
          <div
            className="table-scroll-container"
            style={{
              maxHeight: "600px",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
          >
            <Table columns={columns} data={questions} showSerialNumber={true} />
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="pagination-container">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Previous page"
              >
                ←
              </button>

              {/* Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage =
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  if (!showPage) {
                    // Show ellipsis
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          style={{
                            padding: "8px 4px",
                            color: "#9ca3af",
                            fontSize: "14px",
                          }}
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-btn ${page === currentPage ? "active" : ""}`}
                    >
                      {page}
                    </button>
                  );
                },
              )}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Next page"
              >
                →
              </button>

              {/* Page Info */}
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Question Popup */}
      <AddBoardQuestion
        isOpen={showAddQuestionPopup}
        onClose={() => {
          setShowAddQuestionPopup(false);
          dispatch(setEditData(null));
        }}
        onSuccess={() => {
          handleRefreshQuestions();
          dispatch(setEditData(null));
        }}
      />

      {showUploadQuestionPopup && (
        <UploadQuestionsTab
          isOpen={showUploadQuestionPopup}
          onClose={() => {
            setShowUploadQuestionPopup(false);
          }}
          onSuccess={() => {
            handleRefreshQuestions();
          }}
        />
      )}
    </div>
  );
};

export default QuestionBank;
